import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { settingsAPI, whatsappAPI, customerAPI } from '../utils/api';
import { sendWhatsappBatch, whatsappErrorMessage } from '../utils/whatsappMarketing';
import { greetingTemplates, TEMPLATE_CATEGORIES } from '../utils/messageTemplates';
import { useTheme } from '../contexts/ThemeContext';

const CONTACTS_PAGE_SIZE = 200;
const COMPOSE_PAGE_SIZE = 150;
const DELIVERY_LOG_KEY = 'billing_marketing_delivery_log_v1';

const getCustomerNormalizedPhone = (c) => {
  if (!c) return '';
  const raw = (c.phones && c.phones[0]) || c.mobile || c.mobile2 || c.mobile3 || '';
  return normalizePhone(raw);
};

/* ── Clean name: strip common prefixes/suffixes, keep only person name ── */
const NOISE_WORDS = /\b(customer|cust|dealer|supplier|distributor|wholesaler|retailer|vendor|client|shop|store|trader|agency|enterprise|enterprises|pvt|ltd|limited|private|mr|mrs|ms|shri|smt|dr|prof|sir|madam|ji)\b/gi;
const cleanNameStr = (raw) => {
  if (!raw) return '';
  let cleaned = raw.replace(NOISE_WORDS, '').replace(/[_\-]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
  // Capitalize each word
  cleaned = cleaned.replace(/\b\w/g, c => c.toUpperCase());
  return cleaned || raw;
};

const normalizePhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

const PaginationBar = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  darkMode,
  border,
  acc,
  muted,
  pg,
}) => {
  if (totalItems === 0) return null;

  const startItem = pageSize === 'all' ? 1 : Math.min((currentPage - 1) * pageSize + 1, totalItems);
  const endItem = pageSize === 'all' ? totalItems : Math.min(currentPage * pageSize, totalItems);

  const getPageNumbers = () => {
    if (pageSize === 'all' || totalPages <= 1) return [];
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div
      style={{
        padding: '10px 14px',
        borderTop: `1px solid ${border}`,
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...muted, flexWrap: 'wrap' }}>
        <span>
          Showing <strong style={{ color: pg.color }}>{startItem}–{endItem}</strong> of <strong style={{ color: pg.color }}>{totalItems}</strong> contacts
        </span>
        {onPageSizeChange && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
            <span>per page:</span>
            {[50, 100, 200, 'all'].map(size => (
              <button
                key={String(size)}
                type="button"
                onClick={() => onPageSizeChange(size)}
                style={{
                  padding: '2px 7px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: pageSize === size ? 700 : 500,
                  border: `1px solid ${pageSize === size ? acc : border}`,
                  background: pageSize === size ? acc : 'transparent',
                  color: pageSize === size ? '#fff' : muted.color,
                  cursor: 'pointer',
                }}
              >
                {size === 'all' ? 'All' : size}
              </button>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && pageSize !== 'all' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            style={{
              padding: '4px 9px',
              borderRadius: 6,
              border: `1px solid ${border}`,
              background: 'transparent',
              cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage <= 1 ? 0.4 : 1,
              fontWeight: 600,
              color: pg.color,
            }}
          >
            ‹ Prev
          </button>

          {pageNumbers.map((p, idx) => {
            if (p === '...') {
              return (
                <span key={`dots-${idx}`} style={{ padding: '0 4px', ...muted }}>
                  …
                </span>
              );
            }
            const isActive = p === currentPage;
            return (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                style={{
                  padding: '4px 9px',
                  borderRadius: 6,
                  border: `1px solid ${isActive ? acc : border}`,
                  background: isActive ? acc : (darkMode ? '#1E293B' : '#fff'),
                  color: isActive ? '#fff' : pg.color,
                  fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer',
                  minWidth: 28,
                  textAlign: 'center',
                }}
              >
                {p}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            style={{
              padding: '4px 9px',
              borderRadius: 6,
              border: `1px solid ${border}`,
              background: 'transparent',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage >= totalPages ? 0.4 : 1,
              fontWeight: 600,
              color: pg.color,
            }}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};

const Marketing = () => {
  const { darkMode, color } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceRoute = useRef(searchParams.get('source') || null);
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#94A3B8' : '#64748B' };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const border = darkMode ? '#334155' : '#E2E8F0';
  const btnPrimary = (disabled) => ({ padding: '10px 20px', background: disabled ? (darkMode ? '#334155' : '#CBD5E1') : acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer' });

  // ── Main tab ──
  const [mainTab, setMainTab] = useState(searchParams.get('action') === 'compose' ? 'compose' : 'contacts'); // contacts | compose | import

  // ── contacts tab ──
  const [contactTab, setContactTab] = useState('all');
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactPage, setContactPage] = useState(1);
  const [contactPageSize, setContactPageSize] = useState(50);
  const [selectedContactRefs, setSelectedContactRefs] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('family');
  const [bulkWorking, setBulkWorking] = useState(false);

  // ── compose tab ──
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('');
  const [uniqueOnly, setUniqueOnly] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('telegram');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState('');
  const [composePage, setComposePage] = useState(1);
  const [composePageSize, setComposePageSize] = useState(50);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('All');
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);
  const [cleanedNames, setCleanedNames] = useState({}); // { customerId: 'Cleaned Name' }
  const [shopInfo, setShopInfo] = useState({ name: '', address: '', phone: '', owner: '' });
  const [appendSignature, setAppendSignature] = useState(true);

  // ── bulk rate limiting & anti-ban ──
  const [delaySeconds, setDelaySeconds] = useState(8);
  const [batchSize, setBatchSize] = useState(40);
  const [antiBan, setAntiBan] = useState(true);
  const [skipUnregistered, setSkipUnregistered] = useState(true);
  const [sendingProgress, setSendingProgress] = useState(null); // { sent, total, current }
  const sendingAbortRef = useRef(false);

  // Delivery log & duplicate prevention (persisted in localStorage)
  const [deliveryLog, setDeliveryLog] = useState(() => {
    try {
      const saved = localStorage.getItem(DELIVERY_LOG_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [deliveryLogFilter, setDeliveryLogFilter] = useState('all'); // all | failed | sent

  const recordDeliveryItem = useCallback((entry) => {
    setDeliveryLog(prev => {
      const normPhone = normalizePhone(entry.phone);
      const idx = prev.findIndex(item => {
        if (item.id && entry.id && item.id === entry.id) return true;
        const existingNorm = normalizePhone(item.phone);
        return normPhone && existingNorm && normPhone === existingNorm;
      });
      let next;
      if (idx >= 0) {
        next = [...prev];
        next[idx] = { ...next[idx], ...entry };
      } else {
        next = [entry, ...prev];
      }
      try {
        localStorage.setItem(DELIVERY_LOG_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });

    // Persist channel readiness to backend & update memory state immediately
    if (entry.whatsapp_ready !== undefined && (entry.id || entry.phone)) {
      if (typeof settingsAPI?.updateChannelReadiness === 'function') {
        settingsAPI.updateChannelReadiness([{
          id: entry.id,
          phone: entry.phone,
          whatsapp_ready: entry.whatsapp_ready,
        }]).catch(err => console.warn('Failed to persist channel readiness:', err));
      }
      const norm = normalizePhone(entry.phone);
      setContacts(prev => prev.map(c => {
        const match = (entry.id && c.id === entry.id) || (norm && normalizePhone(c.mobile || c.phones?.[0]) === norm);
        return match ? { ...c, whatsapp_ready: entry.whatsapp_ready } : c;
      }));
      setAllCustomers(prev => prev.map(c => {
        const match = (entry.id && c.id === entry.id) || (norm && normalizePhone(c.mobile || c.mobile2 || c.mobile3) === norm);
        return match ? { ...c, whatsapp_ready: entry.whatsapp_ready } : c;
      }));
    }
  }, []);

  const clearDeliveryLog = () => {
    if (window.confirm('Clear all temporary delivery confirmation and failure history?')) {
      setDeliveryLog([]);
      try {
        localStorage.removeItem(DELIVERY_LOG_KEY);
      } catch {}
    }
  };

  const sentPhoneKeys = useMemo(() => {
    const set = new Set();
    deliveryLog.forEach(item => {
      if (item.status === 'sent') {
        const key = normalizePhone(item.phone);
        if (key) set.add(key);
      }
    });
    return set;
  }, [deliveryLog]);

  const sentCustomerIds = useMemo(() => {
    return new Set(deliveryLog.filter(item => item.status === 'sent').map(item => item.id));
  }, [deliveryLog]);

  const failedCustomerIds = useMemo(() => {
    return deliveryLog
      .filter(item => item.status === 'failed')
      .filter(item => {
        const key = normalizePhone(item.phone);
        return !key || !sentPhoneKeys.has(key);
      })
      .map(item => item.id);
  }, [deliveryLog, sentPhoneKeys]);

  const filteredDeliveryLog = useMemo(() => {
    if (deliveryLogFilter === 'sent') return deliveryLog.filter(i => i.status === 'sent');
    if (deliveryLogFilter === 'failed') return deliveryLog.filter(i => i.status === 'failed');
    return deliveryLog;
  }, [deliveryLog, deliveryLogFilter]);

  const handleRetryFailedOnly = () => {
    const failedItems = deliveryLog.filter(item => {
      if (item.status !== 'failed') return false;
      const key = normalizePhone(item.phone);
      return !key || !sentPhoneKeys.has(key);
    });
    if (failedItems.length === 0) {
      alert('No failed recipients to retry.');
      return;
    }
    const ids = failedItems.map(item => item.id).filter(Boolean);
    setSelected(ids);
    setFilter('selected');
    setResult({
      success: true,
      msg: `Selected ${ids.length} failed customer(s) to retry. Confirmed sent numbers are excluded to prevent duplicate sending.`
    });
  };

  const handleSwitchToSmsForFailed = () => {
    const failedItems = deliveryLog.filter(item => {
      if (item.status !== 'failed') return false;
      const key = normalizePhone(item.phone);
      return !key || !sentPhoneKeys.has(key);
    });
    if (failedItems.length === 0) {
      alert('No failed recipients to switch to SMS.');
      return;
    }
    const ids = failedItems.map(item => item.id).filter(Boolean);
    setSelected(ids);
    setFilter('selected');
    setChannel('sms');
    setResult({
      success: true,
      msg: `Switched channel to SMS for ${ids.length} failed recipient(s). Maximum contacts will receive your message via SMS fallback.`
    });
  };

  const handleSwitchToEmailForFailed = () => {
    const failedItems = deliveryLog.filter(item => {
      if (item.status !== 'failed') return false;
      const key = normalizePhone(item.phone);
      if (key && sentPhoneKeys.has(key)) return false;
      const cust = allCustomers.find(c => c.id === item.id);
      return !!(cust && cust.email);
    });
    if (failedItems.length === 0) {
      alert('None of the failed recipients have an email address.');
      return;
    }
    const ids = failedItems.map(item => item.id).filter(Boolean);
    setSelected(ids);
    setFilter('selected');
    setChannel('email');
    setResult({
      success: true,
      msg: `Switched channel to Email for ${ids.length} failed recipient(s) with email addresses.`
    });
  };

  const [syncingWaSent, setSyncingWaSent] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pastedNumbers, setPastedNumbers] = useState('');

  const handleSyncWaSent = async () => {
    setSyncingWaSent(true);
    try {
      const res = await whatsappAPI.sentToday(24);
      const sent = res.data?.sent || [];
      if (sent.length === 0) {
        alert('No outgoing WhatsApp messages found in the last 24 hours on your connected WhatsApp session.');
        setSyncingWaSent(false);
        return;
      }
      sent.forEach(item => {
        recordDeliveryItem({
          phone: item.phone,
          name: item.name || 'Customer',
          status: 'sent',
          messageId: item.messageId || 'synced-from-wa',
          timestamp: item.timestamp,
          whatsapp_ready: true,
        });
      });
      const syncedPhones = new Set(sent.map(s => normalizePhone(s.phone)).filter(Boolean));
      setSelected(prev => prev.filter(id => {
        const c = allCustomers.find(cust => cust.id === id);
        const p = getCustomerNormalizedPhone(c);
        return !p || !syncedPhones.has(p);
      }));
      setResult({
        success: true,
        msg: `Synced ${sent.length} sent contact(s) from WhatsApp. They are now recorded as Sent and excluded from remaining sends.`
      });
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.error || 'Failed to sync sent messages from WhatsApp. Make sure WhatsApp is connected.');
    } finally {
      setSyncingWaSent(false);
    }
  };

  const handleApplyPastedNumbers = () => {
    const chunks = pastedNumbers.split(/[\n,;\t\r]+/);
    const extracted = [];
    chunks.forEach(chunk => {
      const matches = chunk.match(/(?:\+?91)?[6-9]\d{9}/g) || chunk.match(/\d{10,12}/g) || [];
      matches.forEach(m => {
        const norm = normalizePhone(m);
        if (norm && norm.length === 10) {
          extracted.push(norm);
        }
      });
    });
    const direct10 = pastedNumbers.match(/\b\d{10}\b/g) || [];
    direct10.forEach(d => extracted.push(d));

    const unique = [...new Set(extracted)];
    if (unique.length === 0) {
      alert('No 10-digit phone numbers detected in the pasted text.');
      return;
    }
    unique.forEach(phone => {
      recordDeliveryItem({
        phone,
        name: 'Previously Contacted',
        status: 'sent',
        messageId: 'manual-skip',
        timestamp: new Date().toISOString(),
        whatsapp_ready: true,
      });
    });
    const set = new Set(unique);
    setSelected(prev => prev.filter(id => {
      const c = allCustomers.find(cust => cust.id === id);
      const p = getCustomerNormalizedPhone(c);
      return !p || !set.has(p);
    }));
    setShowPasteModal(false);
    setPastedNumbers('');
    setResult({
      success: true,
      msg: `Marked ${unique.length} phone number(s) as Sent. They are now excluded from sending to prevent duplicate messages.`
    });
  };

  const handleSelectRemainingUnsent = () => {
    const unsentCusts = allCustomers.filter(c => {
      const key = getCustomerNormalizedPhone(c);
      return !sentCustomerIds.has(c.id) && (!key || !sentPhoneKeys.has(key));
    });
    if (unsentCusts.length === 0) {
      alert('All customers in your list have already received this message.');
      return;
    }
    const unsentIds = unsentCusts.map(c => c.id);
    setSelected(unsentIds);
    setFilter('selected');
    setResult({
      success: true,
      msg: `Selected ${unsentIds.length} remaining unsent customer(s). Sent contacts (${sentPhoneKeys.size}) are skipped.`
    });
  };

  // ── WhatsApp Web connection ──
  const [waConnected, setWaConnected] = useState(false);
  const [waQR, setWaQR] = useState(null);
  const [waInfo, setWaInfo] = useState(null);
  const [waServiceDown, setWaServiceDown] = useState(false);
  const [waStatusError, setWaStatusError] = useState('');
  const [waState, setWaState] = useState('starting');
  const [waRefreshing, setWaRefreshing] = useState(false);
  const [waLoggingOut, setWaLoggingOut] = useState(false);
  const waPollingRef = useRef(null);
  const waStatusPendingRef = useRef(false);

  // ── import tab ──
  const [importFile, setImportFile] = useState(null);
  const [importStep, setImportStep] = useState(1); // 1=upload, 2=configure, 3=result
  const [importKeywords, setImportKeywords] = useState('');
  const [rebuildUniverse, setRebuildUniverse] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInputRef = useRef(null);
  const [promotingId, setPromotingId] = useState('');

  // Clean URL params after reading
  useEffect(() => {
    if (searchParams.get('action') || searchParams.get('source')) {
      setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('action'); next.delete('source'); return next; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Listen for sv-open-modal event (from F8 shortcut / command palette)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'send-message') setMainTab('compose');
    };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, []);

  // ESC key handling
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT')) return;
      if (mainTab !== 'contacts') { setMainTab('contacts'); return; }
      if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [mainTab, navigate]);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const res = await settingsAPI.marketingContacts(contactTab, contactSearch || undefined);
      setContacts(res.data || []);
    } catch (e) { console.error(e); setContacts([]); }
    setContactsLoading(false);
  }, [contactTab, contactSearch]);

  /* ── Load Contacts ── */
  useEffect(() => {
    if (mainTab === 'contacts') loadContacts();
  }, [mainTab, loadContacts]);

  const filteredContacts = contacts;

  useEffect(() => {
    setContactPage(1);
  }, [contactTab, contactSearch, contactPageSize]);

  const pagedContacts = useMemo(() => {
    if (contactPageSize === 'all') return filteredContacts;
    const size = typeof contactPageSize === 'number' ? contactPageSize : 50;
    const start = (contactPage - 1) * size;
    return filteredContacts.slice(start, start + size);
  }, [filteredContacts, contactPage, contactPageSize]);

  const totalContactPages = useMemo(() => {
    if (contactPageSize === 'all') return 1;
    const size = typeof contactPageSize === 'number' ? contactPageSize : 50;
    return Math.max(1, Math.ceil(filteredContacts.length / size));
  }, [filteredContacts, contactPageSize]);

  const toggleContactSelect = (id) => {
    setSelectedContactRefs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAllVisibleContacts = () => {
    const all = pagedContacts.map(c => c.id);
    const allSelected = all.length > 0 && all.every(id => selectedContactRefs.includes(id));
    if (allSelected) {
      setSelectedContactRefs(prev => prev.filter(id => !all.includes(id)));
    } else {
      setSelectedContactRefs(prev => [...new Set([...prev, ...all])]);
    }
  };

  const applyBulkCategory = async () => {
    if (selectedContactRefs.length === 0) return;
    setBulkWorking(true);
    try {
      await settingsAPI.bulkCategoryMarketingContacts(selectedContactRefs, bulkCategory);
      await loadContacts();
      await loadCustomers();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk category update failed');
    } finally {
      setBulkWorking(false);
    }
  };

  const allFilteredContactIds = useMemo(() => filteredContacts.map(c => c.id), [filteredContacts]);
  const allFilteredContactsSelected = allFilteredContactIds.length > 0 && allFilteredContactIds.every(id => selectedContactRefs.includes(id));

  const toggleAllFilteredContacts = () => {
    if (allFilteredContactsSelected) {
      setSelectedContactRefs(prev => prev.filter(id => !allFilteredContactIds.includes(id)));
    } else {
      setSelectedContactRefs(prev => [...new Set([...prev, ...allFilteredContactIds])]);
    }
  };

  const prepareAndSelectContactsForCompose = useCallback(async (targetIds, contactObjects = []) => {
    let currentAll = allCustomers;
    if (currentAll.length === 0) {
      setLoading(true);
      try {
        const res = await settingsAPI.marketingContacts('all');
        currentAll = Array.isArray(res.data) ? res.data.map(c => ({
          ...c,
          mobile: c.mobile || c.phone || c.phones?.[0] || '',
          mobile2: c.mobile2 || c.phones?.[1] || '',
          mobile3: c.mobile3 || c.phones?.[2] || '',
          email: c.email || '',
          tags: c.tags || '',
        })) : [];
        setAllCustomers(currentAll);
        setAllTags([...new Set(currentAll.flatMap(c => c.tags ? c.tags.split(',').map(t => t.trim()).filter(Boolean) : []))].sort((a, b) => a.localeCompare(b)));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    const targetIdSet = new Set(targetIds);
    const selectedObjs = contactObjects.length > 0
      ? contactObjects
      : contacts.filter(c => targetIdSet.has(c.id));

    const selectedPhones = new Set(
      selectedObjs.flatMap(c => (c.phones || [c.mobile]).map(normalizePhone).filter(Boolean))
    );

    const matchedIds = new Set();
    currentAll.forEach(c => {
      const cPhones = [c.mobile, c.mobile2, c.mobile3, ...(c.phones || [])].map(normalizePhone).filter(Boolean);
      if (targetIdSet.has(c.id) || cPhones.some(p => selectedPhones.has(p))) {
        matchedIds.add(c.id);
      }
    });

    const missingObjs = selectedObjs.filter(obj => {
      const objPhones = (obj.phones || [obj.mobile]).map(normalizePhone).filter(Boolean);
      return !currentAll.some(ac => ac.id === obj.id || [ac.mobile, ac.mobile2, ac.mobile3].map(normalizePhone).some(p => objPhones.includes(p)));
    }).map(c => ({
      ...c,
      mobile: c.mobile || c.phones?.[0] || '',
      mobile2: c.mobile2 || c.phones?.[1] || '',
      mobile3: c.mobile3 || c.phones?.[2] || '',
      email: c.email || '',
      tags: c.tags || '',
    }));

    if (missingObjs.length > 0) {
      setAllCustomers(prev => [...prev, ...missingObjs]);
      missingObjs.forEach(m => matchedIds.add(m.id));
    }

    targetIds.forEach(id => matchedIds.add(id));

    setCleanedNames(prev => {
      const next = { ...prev };
      [...currentAll, ...missingObjs].forEach(c => {
        if (!(c.id in next)) next[c.id] = cleanNameStr(c.name);
      });
      return next;
    });

    setSelected([...matchedIds]);
    setFilter('selected');
    setMainTab('compose');
  }, [allCustomers, contacts]);

  const handleSendToSelectedContacts = () => {
    if (selectedContactRefs.length === 0) return;
    const selectedObjs = contacts.filter(c => selectedContactRefs.includes(c.id));
    prepareAndSelectContactsForCompose(selectedContactRefs, selectedObjs);
  };

  const handleSendToSingleContact = (contact) => {
    if (!contact) return;
    prepareAndSelectContactsForCompose([contact.id], [contact]);
  };

  const bulkPromoteImported = async (target) => {
    const importedIds = selectedContactRefs
      .filter(r => String(r).startsWith('imported:'))
      .map(r => String(r).split(':', 2)[1]);
    if (importedIds.length === 0) return;
    setBulkWorking(true);
    try {
      await settingsAPI.promoteMarketingContacts(importedIds, target);
      await loadContacts();
      await loadCustomers();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk promote failed');
    } finally {
      setBulkWorking(false);
    }
  };

  /* ── Load shop settings for signature ── */
  useEffect(() => {
    if (mainTab !== 'compose') return;
    settingsAPI.get().then(res => {
      const s = res.data || {};
      setShopInfo({ name: s.shop_name || '', address: s.shop_address || '', phone: s.shop_phone || '', owner: s.owner_name || '' });
    }).catch(() => {});
  }, [mainTab]);

  // Build signature text
  const shopSignature = (() => {
    const parts = [];
    if (shopInfo.name) parts.push(`*${shopInfo.name}*`);
    if (shopInfo.address) parts.push(shopInfo.address);
    if (shopInfo.owner) parts.push(shopInfo.owner);
    if (shopInfo.phone) parts.push('\u260E\uFE0F ' + shopInfo.phone);
    return parts.length > 0 ? '\n\nWith regards,\n' + parts.join('\n') : '';
  })();

  const refreshWaStatus = useCallback(async () => {
    if (waStatusPendingRef.current) return;
    waStatusPendingRef.current = true;
    setWaRefreshing(true);
    try {
      const res = await whatsappAPI.status();
      const d = res.data;
      if (!d || typeof d.connected !== 'boolean') throw new Error('Unable to read WhatsApp connection status');
      setWaConnected(d.connected);
      setWaQR(d.qr || null);
      setWaInfo(d.info || null);
      setWaServiceDown(!!d.service_down);
      setWaStatusError(d.error || d.last_error || '');
      setWaState(d.state || (d.connected ? 'connected' : d.qr ? 'qr' : 'starting'));
    } catch (error) {
      setWaServiceDown(true);
      setWaConnected(false);
      setWaQR(null);
      setWaStatusError(whatsappErrorMessage(error));
      setWaState('error');
    } finally {
      waStatusPendingRef.current = false;
      setWaRefreshing(false);
    }
  }, []);

  /* ── WhatsApp Web: poll status when compose tab is active ── */
  useEffect(() => {
    if (mainTab !== 'compose') {
      if (waPollingRef.current) { clearInterval(waPollingRef.current); waPollingRef.current = null; }
      return;
    }
    refreshWaStatus(); // immediate
    waPollingRef.current = setInterval(refreshWaStatus, 3000); // poll every 3s
    return () => { if (waPollingRef.current) { clearInterval(waPollingRef.current); waPollingRef.current = null; } };
  }, [mainTab, refreshWaStatus]);

  const handleWaLogout = async () => {
    setWaLoggingOut(true);
    try {
      await whatsappAPI.logout();
      setWaConnected(false); setWaInfo(null); setWaQR(null);
      await refreshWaStatus();
    } catch (error) {
      setWaStatusError(whatsappErrorMessage(error));
    }
    setWaLoggingOut(false);
  };

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsAPI.marketingContacts('all');
      const list = Array.isArray(res.data) ? res.data.map(c => ({
        ...c,
        mobile: c.mobile || c.phone || c.phones?.[0] || '',
        mobile2: c.mobile2 || c.phones?.[1] || '',
        mobile3: c.mobile3 || c.phones?.[2] || '',
        email: c.email || '',
        tags: c.tags || '',
        whatsapp_ready: c.whatsapp_ready,
        telegram_ready: c.telegram_ready,
        sms_ready: c.sms_ready,
        email_ready: c.email_ready,
      })) : [];
      setAllCustomers(list);
      // Auto-compute cleaned names for new customers (preserve edits for existing)
      setCleanedNames(prev => {
        const next = { ...prev };
        list.forEach(c => { if (!(c.id in next)) next[c.id] = cleanNameStr(c.name); });
        return next;
      });
      setAllTags([...new Set(list.flatMap(c => c.tags ? c.tags.split(',').map(t => t.trim()).filter(Boolean) : []))].sort((a, b) => a.localeCompare(b)));
    } catch (e) { console.error(e); setAllCustomers([]); }
    setLoading(false);
  }, []);

  /* ── Load Customers for compose ── */
  useEffect(() => {
    if (mainTab !== 'compose') return;
    setComposePage(1);
    loadCustomers();
  }, [mainTab, loadCustomers]);

  useEffect(() => {
    setComposePage(1);
  }, [filter, tagFilter, search, uniqueOnly, composePageSize]);

  const selectedIdSet = useMemo(() => new Set(selected), [selected]);

  const matchesMonth = useCallback((value) => {
    if (!value) return false;
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const normalized = String(value);
    if (normalized.startsWith('--')) {
      return normalized.slice(2, 4) === month;
    }
    const parts = normalized.split('-');
    return parts.length >= 2 && parts[1] === month;
  }, []);

  const visibleCustomers = useMemo(() => {
    let list = allCustomers;

    if (filter === 'unique_mobile' || uniqueOnly) {
      const seen = new Set();
      list = list.filter(c => {
        const p = normalizePhone(c.mobile || c.mobile2 || c.mobile3 || c.phone || c.phones?.[0]);
        if (!p || seen.has(p)) return false;
        seen.add(p);
        return true;
      });
    }

    if (filter === 'selected') {
      list = list.filter(c => selectedIdSet.has(c.id));
    } else if (filter === 'wa_ready') {
      list = list.filter(c => c.whatsapp_ready === true);
    } else if (filter === 'sms_ready') {
      list = list.filter(c => c.sms_ready === true || !!(c.mobile || c.mobile2 || c.mobile3 || c.phones?.[0]));
    } else if (filter === 'email_ready') {
      list = list.filter(c => c.email_ready === true || !!c.email);
    } else if (filter === 'not_wa') {
      list = list.filter(c => c.whatsapp_ready === false);
    } else if (filter === 'customers') {
      list = list.filter(c => c.type === 'customer');
    } else if (filter === 'suppliers') {
      list = list.filter(c => c.type === 'supplier');
    } else if (filter === 'imported') {
      list = list.filter(c => c.type === 'imported');
    } else if (filter === 'family') {
      list = list.filter(c => (c.category || '').toLowerCase() === 'family' || (c.tags || '').toLowerCase().includes('family'));
    } else if (filter === 'friends') {
      list = list.filter(c => (c.category || '').toLowerCase() === 'friends' || (c.tags || '').toLowerCase().includes('friends'));
    } else if (filter === 'birthday') {
      list = list.filter(c => matchesMonth(c.birthday));
    } else if (filter === 'anniversary') {
      list = list.filter(c => matchesMonth(c.anniversary));
    } else if (filter === 'email') {
      list = list.filter(c => !!c.email);
    } else if (filter === 'tag' && tagFilter) {
      list = list.filter(c => (c.tags || '').split(',').map(t => t.trim()).filter(Boolean).includes(tagFilter));
    } else if (filter === 'failed') {
      const failedSet = new Set(failedCustomerIds);
      list = list.filter(c => failedSet.has(c.id));
    } else if (filter === 'sent') {
      list = list.filter(c => sentCustomerIds.has(c.id) || sentPhoneKeys.has(getCustomerNormalizedPhone(c)));
    } else if (filter === 'unsent') {
      list = list.filter(c => {
        const key = getCustomerNormalizedPhone(c);
        return !sentCustomerIds.has(c.id) && (!key || !sentPhoneKeys.has(key));
      });
    }

    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter(c => [
      c.name,
      cleanedNames[c.id],
      c.mobile,
      c.mobile2,
      c.mobile3,
      c.email,
      c.tags,
    ].some(value => String(value || '').toLowerCase().includes(q)));
  }, [allCustomers, cleanedNames, filter, matchesMonth, search, tagFilter, uniqueOnly, selectedIdSet, failedCustomerIds, sentCustomerIds, sentPhoneKeys]);

  const pagedVisibleCustomers = useMemo(() => {
    if (composePageSize === 'all') return visibleCustomers;
    const size = typeof composePageSize === 'number' ? composePageSize : 50;
    const start = (composePage - 1) * size;
    return visibleCustomers.slice(start, start + size);
  }, [visibleCustomers, composePage, composePageSize]);
  const visibleCustomerIds = useMemo(() => pagedVisibleCustomers.map(c => c.id), [pagedVisibleCustomers]);
  const allVisibleIds = useMemo(() => visibleCustomers.map(c => c.id), [visibleCustomers]);
  const totalComposePages = useMemo(() => {
    if (composePageSize === 'all') return 1;
    const size = typeof composePageSize === 'number' ? composePageSize : 50;
    return Math.max(1, Math.ceil(visibleCustomers.length / size));
  }, [visibleCustomers, composePageSize]);
  const selectedCustomers = useMemo(
    () => allCustomers.filter(c => selectedIdSet.has(c.id)),
    [allCustomers, selectedIdSet]
  );
  const allVisibleSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIdSet.has(id));

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => {
    if (allVisibleIds.length === 0) return;
    setSelected(prev => (
      allVisibleSelected
        ? prev.filter(id => !allVisibleIds.includes(id))
        : [...new Set([...prev, ...allVisibleIds])]
    ));
  };

  /* ── Filtered & grouped message templates ── */
  const filteredTemplates = useMemo(() => {
    return greetingTemplates.map((t, idx) => ({ ...t, index: idx })).filter(t => {
      if (templateCategoryFilter !== 'All' && t.category !== templateCategoryFilter) return false;
      if (templateSearchTerm.trim()) {
        const q = templateSearchTerm.trim().toLowerCase();
        return (t.label || '').toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q) || (t.text || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [templateCategoryFilter, templateSearchTerm]);

  const groupedTemplates = useMemo(() => {
    const groups = {};
    filteredTemplates.forEach(t => {
      const cat = t.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    });
    return Object.entries(groups).map(([category, items]) => ({ category, items }));
  }, [filteredTemplates]);

  /* ── Template selection ── */
  const handleTemplateSelect = (idx) => {
    setSelectedTemplate(idx);
    if (idx !== '' && greetingTemplates[idx]) setMessage(greetingTemplates[idx].text);
  };

  /* ── Send message ── */
  const handleSend = async () => {
    if (sending) return;
    if (selectedCustomers.length === 0) { alert('Select at least one customer'); return; }
    if (!message.trim()) { alert('Enter a message'); return; }
    if (channel === 'whatsapp' && !waConnected) {
      setResult({ success: false, msg: 'Connect WhatsApp by scanning the QR code before sending.' });
      return;
    }
    // Duplicate prevention: check if any selected customer was already confirmed sent
    const alreadySentList = selectedCustomers.filter(c => {
      const key = getCustomerNormalizedPhone(c);
      return sentCustomerIds.has(c.id) || (key && sentPhoneKeys.has(key));
    });

    let toSendCustomers = selectedCustomers;
    if (alreadySentList.length > 0) {
      toSendCustomers = selectedCustomers.filter(c => {
        const key = getCustomerNormalizedPhone(c);
        return !sentCustomerIds.has(c.id) && (!key || !sentPhoneKeys.has(key));
      });
      if (toSendCustomers.length === 0) {
        setResult({
          success: false,
          msg: `All ${alreadySentList.length} selected customer(s) already received this message. Duplicate sending was prevented. To re-send, clear the delivery log.`
        });
        return;
      }
      const alreadySentIdSet = new Set(alreadySentList.map(c => c.id));
      setSelected(prev => prev.filter(id => !alreadySentIdSet.has(id)));
    }

    setSending(true); setResult(null); sendingAbortRef.current = false;
    setSendingProgress({ sent: 0, total: toSendCustomers.length, current: '' });
    // Append shop signature if enabled
    const finalMsg = appendSignature && shopSignature ? message + shopSignature : message;
    try {
      if (channel === 'whatsapp') {
        const outcome = await sendWhatsappBatch({
          customers: toSendCustomers, batchSize, message: finalMsg, cleanedNames,
          attachment, delaySeconds, send: whatsappAPI.send,
          shouldStop: () => sendingAbortRef.current,
          onProgress: setSendingProgress,
          onSent: id => setSelected(prev => prev.filter(value => value !== id)),
          onItemResult: recordDeliveryItem,
          antiBan,
          skipUnregistered,
        });
        const remaining = toSendCustomers.length - outcome.sent;
        const summary = `WhatsApp: Sent ${outcome.sent}, Failed ${outcome.failed}, Skipped ${outcome.skipped} of ${outcome.total}.`;
        setResult({
          success: outcome.sent > 0 && outcome.failed === 0 && outcome.skipped === 0,
          msg: `${summary}${outcome.stopped ? ' Sending stopped.' : ''}${remaining > 0 ? ` ${remaining} customer(s) remain selected; successful sends were deselected.` : ''}${outcome.errors.length ? ` ${outcome.errors.slice(0, 3).join('; ')}` : ''}`,
        });
        await refreshWaStatus();
      } else if (channel === 'sms') {
        const selCusts = toSendCustomers;
        let count = 0;
        const total = Math.min(selCusts.length, batchSize > 0 ? batchSize : selCusts.length);
        for (let i = 0; i < total; i++) {
          if (sendingAbortRef.current) break;
          const c = selCusts[i];
          const phone = c.mobile || c.mobile2 || c.mobile3;
          if (phone) {
            const dispName = cleanedNames[c.id] || c.name || '';
            const text = finalMsg.replace(/\{name\}/g, dispName).replace(/\{mobile\}/g, phone);
            setSendingProgress({ sent: count, total, current: dispName || phone });
            window.open(`sms:+91${phone}?body=${encodeURIComponent(text)}`, '_blank');
            recordDeliveryItem({
              id: c.id,
              name: dispName || c.name || 'Customer',
              phone,
              status: 'sent',
              messageId: 'sms-opened',
              timestamp: new Date().toISOString(),
              channel: 'sms',
            });
            count++;
            if (i < total - 1 && delaySeconds > 0) {
              await new Promise(r => setTimeout(r, delaySeconds * 1000));
            }
          }
        }
        setSendingProgress(null);
        setResult({ success: true, msg: `Opened SMS for ${count} customer(s).` });
      } else {
        // Telegram / Email / Both — server-side with attachment support
        const formData = new FormData();
        formData.append('customer_ids', JSON.stringify(selected));
        formData.append('message', finalMsg);
        formData.append('channel', channel);
        formData.append('cleaned_names', JSON.stringify(cleanedNames));
        formData.append('delay_seconds', String(delaySeconds));
        formData.append('batch_size', String(batchSize));
        if (attachment) {
          formData.append('attachment', attachment);
        }
        setSendingProgress({ sent: 0, total: selected.length, current: 'Sending via server...' });
        const res = await settingsAPI.sendMessage(formData);
        setSendingProgress(null);
        setResult({ success: true, msg: `Sent: ${res.data.sent || 0}, Failed: ${res.data.failed || 0} via ${(res.data.channels || [channel]).join(', ')}${res.data.batch_limited ? ` (limited to ${batchSize} per batch)` : ''}` });
      }
    } catch (e) {
      setResult({ success: false, msg: e.response?.data?.detail || 'Failed to send messages' });
    } finally {
      setSendingProgress(null);
      setSending(false);
    }
  };

  /* ── Contact Import (.csv/.vcf) ── */
  const handleImportFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) { setImportFile(f); setImportStep(2); setImportResult(null); }
  };
  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true); setImportResult(null);
    try {
      const res = await customerAPI.importVCF(importFile, {
        keywords: importKeywords || undefined,
        rebuild: rebuildUniverse,
      });
      setImportResult(res.data);
      setImportStep(3);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d ? JSON.stringify(d) : 'Import failed');
      setImportResult({ error: msg });
      setImportStep(3);
    }
    setImporting(false);
  };
  const resetImport = () => { setImportFile(null); setImportStep(1); setImportResult(null); setImportKeywords(''); setRebuildUniverse(true); };

  const promoteImportedContact = async (contactId, target) => {
    setPromotingId(`${target}:${contactId}`);
    try {
      await settingsAPI.promoteMarketingContacts([contactId], target);
      await loadContacts();
      await loadCustomers();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to promote contact');
    } finally {
      setPromotingId('');
    }
  };

  const filterOptions = [
    { value: 'all', label: '\u{1F465} All' },
    { value: 'wa_ready', label: '📲 WA Ready' },
    { value: 'sms_ready', label: '📱 SMS Ready' },
    { value: 'email_ready', label: '📬 Email Ready' },
    { value: 'not_wa', label: '⚠️ Not on WA' },
    { value: 'unique_mobile', label: '\u{1F4F1} Unique No.' },
    { value: 'customers', label: '\u{1F465} Customers' },
    { value: 'suppliers', label: '\u{1F3ED} Suppliers' },
    { value: 'imported', label: '\u{1F4E5} Imported' },
    { value: 'family', label: '\u{1F468}\u{200D}\u{1F469}\u{200D}\u{1F467}\u{200D}\u{1F466} Family' },
    { value: 'friends', label: '\u{1F91D} Friends' },
    { value: 'birthday', label: '\u{1F382} Birthday' },
    { value: 'anniversary', label: '\u{1F48D} Anniversary' },
    { value: 'email', label: '\u{1F4E7} Email' },
    { value: 'tag', label: '\u{1F3F7}\u{FE0F} Tag' },
    ...(selected.length > 0 ? [{ value: 'selected', label: `\u{2705} Selected (${selected.length})` }] : []),
    ...(failedCustomerIds.length > 0 ? [{ value: 'failed', label: `❌ Failed (${failedCustomerIds.length})` }] : []),
    ...(sentCustomerIds.size > 0 || sentPhoneKeys.size > 0 ? [{ value: 'sent', label: `✓ Sent (${sentPhoneKeys.size || sentCustomerIds.size})` }] : []),
    ...(sentPhoneKeys.size > 0 || sentCustomerIds.size > 0 ? [{ value: 'unsent', label: `⏳ Remaining Unsent` }] : []),
  ];

  const mainTabs = [
    { key: 'contacts', label: '📱 All Contacts', desc: 'View all phone numbers' },
    { key: 'compose', label: '✉️ Compose & Send', desc: 'Create & send messages' },
    { key: 'import', label: '📥 Import Contacts', desc: 'Import VCF/contacts' },
  ];
  const sendDisabled = sending || selectedCustomers.length === 0 || !message.trim() || (channel === 'whatsapp' && (!waConnected || waLoggingOut));

  return (
    <Layout>
      <div className="page-enter max-w-7xl mx-auto">
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>Marketing & CRM</h1>
          <p style={{ ...muted, marginTop: 4, fontSize: 14 }}>Manage contacts, send messages, import contact files</p>
        </div>

        {/* Main Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {mainTabs.map(t => (
            <button key={t.key} onClick={() => setMainTab(t.key)}
              style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700, border: `2px solid ${mainTab === t.key ? acc : border}`,
                background: mainTab === t.key ? acc : (darkMode ? '#1E293B' : '#fff'),
                color: mainTab === t.key ? '#fff' : (darkMode ? '#94A3B8' : '#64748B'), cursor: 'pointer', transition: 'all 0.2s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══════ CONTACTS TAB ═══════ */}
        {mainTab === 'contacts' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {['all', 'unique', 'customers', 'suppliers', 'imported', 'attention', 'family', 'friends'].map(t => (
                <button key={t} onClick={() => setContactTab(t)}
                  style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                    background: contactTab === t ? acc : (darkMode ? '#1E293B' : '#F1F5F9'),
                    color: contactTab === t ? '#fff' : (darkMode ? '#94A3B8' : '#64748B') }}>
                  {t === 'all' ? '🌐 All' : t === 'customers' ? '👥 Customers' : t === 'suppliers' ? '🏭 Suppliers' : t === 'imported' ? '📥 Imported' : t === 'attention' ? '⚠️ Attention' : t === 'family' ? '👨‍👩‍👧‍👦 Family' : '🤝 Friends'}
                </button>
              ))}
            </div>

            <div style={{ ...card, padding: 16, marginBottom: 12 }}>
              <input type="text" placeholder={`Search ${contactTab}...`} value={contactSearch}
                onChange={e => setContactSearch(e.target.value)} style={inp} />
            </div>

            <div style={{ ...card, padding: 12, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={toggleAllVisibleContacts}
                style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: `1px solid ${border}`, cursor: 'pointer' }}>
                {pagedContacts.length > 0 && pagedContacts.every(c => selectedContactRefs.includes(c.id)) ? 'Deselect Page' : `Select Page (${pagedContacts.length})`}
              </button>
              {filteredContacts.length > CONTACTS_PAGE_SIZE && (
                <button onClick={toggleAllFilteredContacts}
                  style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: `1px solid ${border}`, cursor: 'pointer' }}>
                  {allFilteredContactsSelected ? 'Deselect All' : `Select All (${filteredContacts.length})`}
                </button>
              )}
              {selectedContactRefs.length > 0 && (
                <button onClick={() => setSelectedContactRefs([])}
                  style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', background: 'transparent', color: muted.color, cursor: 'pointer' }}>
                  Clear ({selectedContactRefs.length})
                </button>
              )}
              <div style={{ height: 24, width: 1, background: border, margin: '0 4px' }} />
              <button
                disabled={selectedContactRefs.length === 0}
                onClick={handleSendToSelectedContacts}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  border: 'none',
                  cursor: selectedContactRefs.length === 0 ? 'not-allowed' : 'pointer',
                  background: selectedContactRefs.length === 0 ? (darkMode ? '#334155' : '#CBD5E1') : '#10B981',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: selectedContactRefs.length > 0 ? '0 2px 4px rgba(16, 185, 129, 0.3)' : 'none',
                }}
              >
                <span>?? Send Message to Selected ({selectedContactRefs.length})</span>
              </button>
              <div style={{ height: 24, width: 1, background: border, margin: '0 4px' }} />
              <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)} style={{ ...inp, width: 190, padding: '8px 10px' }}>
                <option value="family">Set Group: Family</option>
                <option value="friends">Set Group: Friends</option>
                <option value="customer">Set Group: Customer</option>
                <option value="supplier">Set Group: Supplier</option>
                <option value="general">Set Group: General</option>
              </select>
              <button disabled={bulkWorking || selectedContactRefs.length === 0} onClick={applyBulkCategory}
                style={{ ...btnPrimary(bulkWorking || selectedContactRefs.length === 0), padding: '8px 12px', fontSize: 12 }}>
                {bulkWorking ? 'Working...' : `Apply Group (${selectedContactRefs.length})`}
              </button>
              <button disabled={bulkWorking || selectedContactRefs.filter(r => String(r).startsWith('imported:')).length === 0} onClick={() => bulkPromoteImported('customer')}
                style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: `1px solid ${border}`, cursor: 'pointer' }}>
                Add Selected Imported -> Customer
              </button>
              <button disabled={bulkWorking || selectedContactRefs.filter(r => String(r).startsWith('imported:')).length === 0} onClick={() => bulkPromoteImported('supplier')}
                style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: `1px solid ${border}`, cursor: 'pointer' }}>
                Add Selected Imported -> Supplier
              </button>
            </div>

            <div style={card}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}` }}>
                <h3 style={{ ...pg, fontWeight: 600, fontSize: 14, margin: 0 }}>
                  {contactsLoading ? 'Loading...' : `${filteredContacts.length} ${contactTab}`}
                </h3>
              </div>
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {contactsLoading ? (
                  <div style={{ textAlign: 'center', padding: 32, ...muted }}>Loading...</div>
                ) : filteredContacts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 32, ...muted }}>No {contactTab} found</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${border}` }}>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 16px', fontWeight: 700, width: 36 }}>
                          <input type="checkbox" checked={pagedContacts.length > 0 && pagedContacts.every(c => selectedContactRefs.includes(c.id))} onChange={toggleAllVisibleContacts} style={{ width: 14, height: 14, accentColor: acc }} />
                        </th>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 16px', fontWeight: 700 }}>Name</th>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Phone(s)</th>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Email</th>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Channels</th>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Type</th>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Category</th>
                        <th style={{ ...pg, textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedContacts.map((c, i) => (
                        <tr key={c.id || i} style={{ borderTop: i > 0 ? `1px solid ${border}` : 'none', background: (c.duplicate_phones?.length || c.duplicate_email) ? (darkMode ? '#422006' : '#fef3c7') : 'transparent' }}>
                          <td style={{ padding: '10px 16px' }}>
                            <input type="checkbox" checked={selectedContactRefs.includes(c.id)} onChange={() => toggleContactSelect(c.id)} style={{ width: 14, height: 14, accentColor: acc }} />
                          </td>
                          <td style={{ ...pg, padding: '10px 16px', fontWeight: 600 }}>
                            {c.name}
                            {(c.duplicate_phones?.length > 0 || c.duplicate_email) && (
                              <span title={`Duplicate: ${[...(c.duplicate_phones || []), c.duplicate_email ? 'email' : ''].filter(Boolean).join(', ')}`}
                                style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#dc2626', color: '#fff', fontWeight: 700 }}>DUP</span>
                            )}
                          </td>
                          <td style={{ ...muted, padding: '10px 12px' }}>
                            {(c.phones || []).length > 0
                              ? c.phones.map((p, j) => <span key={j} style={{ display: 'inline-block', marginRight: 8, background: (c.duplicate_phones || []).includes(p) ? '#fecaca' : (darkMode ? '#0F172A' : '#F1F5F9'), padding: '2px 8px', borderRadius: 4, fontSize: 12, color: (c.duplicate_phones || []).includes(p) ? '#991b1b' : undefined, fontWeight: (c.duplicate_phones || []).includes(p) ? 700 : undefined }}>{p}</span>)
                              : <span style={{ opacity: 0.5 }}>—</span>}
                          </td>
                          <td style={{ ...muted, padding: '10px 12px', fontSize: 12, color: c.duplicate_email ? '#dc2626' : undefined, fontWeight: c.duplicate_email ? 700 : undefined }}>{c.email || '—'}</td>
                          <td style={{ ...muted, padding: '10px 12px', fontSize: 11 }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {c.whatsapp_ready === true && (
                                <span title="Verified on WhatsApp" style={{ fontSize: 9, padding: '2px 5px', borderRadius: 4, background: '#DCFCE7', color: '#15803D', fontWeight: 700 }}>💬 WA</span>
                              )}
                              {c.whatsapp_ready === false && (
                                <span title="Not registered on WhatsApp" style={{ fontSize: 9, padding: '2px 5px', borderRadius: 4, background: '#FEE2E2', color: '#DC2626', fontWeight: 700 }}>⚠️ No WA</span>
                              )}
                              {(c.sms_ready === true || (c.phones && c.phones.length > 0)) && (
                                <span title="SMS Capable" style={{ fontSize: 9, padding: '2px 5px', borderRadius: 4, background: '#E0F2FE', color: '#0369A1', fontWeight: 600 }}>📱 SMS</span>
                              )}
                              {(c.email_ready === true || !!c.email) && (
                                <span title="Email Capable" style={{ fontSize: 9, padding: '2px 5px', borderRadius: 4, background: '#F3E8FF', color: '#7E22CE', fontWeight: 600 }}>📧 Email</span>
                              )}
                            </div>
                          </td>
                          <td style={{ ...muted, padding: '10px 12px', fontSize: 12 }}>{c.type || '—'}</td>
                          <td style={{ ...muted, padding: '10px 12px', fontSize: 12 }}>{c.category || '—'}{c.is_attention && c.attention_reason ? ` (${c.attention_reason})` : ''}</td>
                          <td style={{ ...muted, padding: '10px 12px', fontSize: 12 }}>
                            {c.type === 'imported' ? (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button
                                  disabled={!!c.promoted_to_customer_id || !!c.is_attention || promotingId === `customer:${c.entity_id}`}
                                  onClick={() => promoteImportedContact(c.entity_id, 'customer')}
                                  style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}
                                >
                                  {c.promoted_to_customer_id ? 'Added Customer' : c.is_attention ? 'Needs Attention' : (promotingId === `customer:${c.entity_id}` ? 'Adding...' : 'Add Customer')}
                                </button>
                                <button
                                  disabled={!!c.promoted_to_supplier_id || !!c.is_attention || promotingId === `supplier:${c.entity_id}`}
                                  onClick={() => promoteImportedContact(c.entity_id, 'supplier')}
                                  style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}
                                >
                                  {c.promoted_to_supplier_id ? 'Added Supplier' : c.is_attention ? 'Needs Attention' : (promotingId === `supplier:${c.entity_id}` ? 'Adding...' : 'Add Supplier')}
                                </button>
                              </div>
                            ) : (
                              <span style={{ opacity: 0.6 }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {!contactsLoading && (
                <PaginationBar
                  currentPage={contactPage}
                  totalPages={totalContactPages}
                  totalItems={filteredContacts.length}
                  pageSize={contactPageSize}
                  onPageChange={setContactPage}
                  onPageSizeChange={setContactPageSize}
                  darkMode={darkMode}
                  border={border}
                  acc={acc}
                  muted={muted}
                  pg={pg}
                />
              )}
            </div>
          </div>
        )}

        {/* ═══════ COMPOSE TAB ═══════ */}
        {mainTab === 'compose' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Customer Selection */}
            <div className="lg:col-span-2 space-y-4">
              <div style={{ ...card, padding: 16 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {filterOptions.map(f => (
                    <button key={f.value} onClick={() => setFilter(f.value)}
                      style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                        background: filter === f.value ? acc : (darkMode ? '#0F172A' : '#F1F5F9'),
                        color: filter === f.value ? '#fff' : (darkMode ? '#94A3B8' : '#64748B') }}>
                      {f.label}
                    </button>
                  ))}
                </div>
                {filter === 'tag' && (
                  <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} style={{ ...inp, marginBottom: 6 }}>
                    <option value="">Select a tag...</option>
                    {allTags.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                <input type="text" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} style={inp} />
              </div>

              <div style={card}>
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <h3 style={{ ...pg, fontWeight: 600, fontSize: 13, margin: 0 }}>
                    {loading ? 'Loading...' : search || filter !== 'all' || tagFilter || uniqueOnly ? `${visibleCustomers.length} of ${allCustomers.length} customers` : `${allCustomers.length} customers`}
                    {selected.length > 0 && <span style={{ marginLeft: 6, color: acc }}>({selected.length} selected)</span>}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    {selectedContactRefs.length > 0 && (
                      <button
                        onClick={handleSendToSelectedContacts}
                        style={{
                          fontSize: 12,
                          color: '#10B981',
                          background: darkMode ? '#064E3B22' : '#ECFDF5',
                          border: '1px solid #10B981',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontWeight: 700,
                        }}
                        title="Load selections from All Contacts tab"
                      >
                        📲 Use {selectedContactRefs.length} Selected from Contacts
                      </button>
                    )}
                    {(sentCustomerIds.size > 0 || sentPhoneKeys.size > 0) && (
                      <button
                        type="button"
                        onClick={handleSelectRemainingUnsent}
                        style={{
                          fontSize: 12,
                          color: '#2563EB',
                          background: darkMode ? '#1E3A5F33' : '#EFF6FF',
                          border: '1px solid #3B82F6',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontWeight: 700,
                        }}
                        title="Select all customers who have not yet received this message"
                      >
                        🎯 Select Remaining Unsent ({allCustomers.filter(c => {
                          const key = getCustomerNormalizedPhone(c);
                          return !sentCustomerIds.has(c.id) && (!key || !sentPhoneKeys.has(key));
                        }).length})
                      </button>
                    )}
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', ...muted }}>
                      <input type="checkbox" checked={uniqueOnly || filter === 'unique_mobile'}
                        disabled={filter === 'unique_mobile'}
                        onChange={e => setUniqueOnly(e.target.checked)}
                        style={{ width: 14, height: 14, accentColor: acc }} />
                      <span>Unique Numbers Only</span>
                    </label>
                    <button onClick={toggleAll} style={{ fontSize: 12, color: acc, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      {allVisibleSelected ? 'Deselect All' : `Select All (${visibleCustomers.length})`}
                    </button>
                  </div>
                </div>
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: 24, ...muted }}>Loading...</div>
                  ) : visibleCustomers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24, ...muted }}>No customers match filter</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${border}` }}>
                          <th style={{ ...pg, textAlign: 'left', padding: '8px 8px 8px 16px', fontWeight: 700, width: 30 }}>
                            <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} style={{ width: 15, height: 15, accentColor: acc }} />
                          </th>
                          <th style={{ ...pg, textAlign: 'left', padding: '8px 6px', fontWeight: 700 }}>Original Name</th>
                          <th style={{ ...pg, textAlign: 'left', padding: '8px 6px', fontWeight: 700 }}>Cleaned Name</th>
                          <th style={{ ...pg, textAlign: 'left', padding: '8px 6px', fontWeight: 700 }}>Contact</th>
                          <th style={{ ...pg, textAlign: 'center', padding: '8px 8px', fontWeight: 700, width: 40 }}>Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedVisibleCustomers.map((c, i) => (
                          <tr key={c.id} style={{ borderTop: i > 0 ? `1px solid ${border}` : 'none',
                            background: selectedIdSet.has(c.id) ? (darkMode ? '#1E3A5F33' : `${acc}10`) : 'transparent' }}>
                            <td style={{ padding: '6px 8px 6px 16px' }}>
                              <input type="checkbox" checked={selectedIdSet.has(c.id)} onChange={() => toggleSelect(c.id)} style={{ width: 15, height: 15, accentColor: acc }} />
                            </td>
                            <td style={{ ...pg, padding: '6px', fontWeight: 500, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                              onClick={() => toggleSelect(c.id)} title={c.name}>
                              {c.name}
                              {sentCustomerIds.has(c.id) || sentPhoneKeys.has(getCustomerNormalizedPhone(c)) ? (
                                <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: '#10B98122', color: '#10B981', fontWeight: 700 }} title="Confirmed Sent">✓ Sent</span>
                              ) : failedCustomerIds.includes(c.id) ? (
                                <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: '#EF444422', color: '#EF4444', fontWeight: 700 }} title="Failed to send">✗ Failed</span>
                              ) : null}
                              {c.category && c.category !== 'general' && (
                                <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#94A3B8' : '#64748B', fontWeight: 600 }}>
                                  {c.category === 'family' ? '??????????? Family' : c.category === 'friends' ? '?? Friends' : c.category === 'supplier' ? '?? Supplier' : c.type === 'customer' ? '?? Customer' : c.category}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '4px 6px' }}>
                              <input type="text" value={cleanedNames[c.id] || ''} onChange={e => setCleanedNames(prev => ({ ...prev, [c.id]: e.target.value }))}
                                style={{ background: darkMode ? '#0F172A' : '#F0FDF4', color: darkMode ? '#6EE7B7' : '#065F46', border: `1px solid ${darkMode ? '#065F46' : '#BBF7D0'}`,
                                  borderRadius: 6, padding: '4px 8px', fontSize: 12, fontWeight: 600, width: '100%', boxSizing: 'border-box', outline: 'none' }} />
                            </td>
                            <td style={{ ...muted, padding: '6px', fontSize: 11, maxWidth: 140 }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${c.mobile || ''}${c.mobile2 ? ', ' + c.mobile2 : ''}${c.email ? ' · ' + c.email : ''}`}>
                                {c.mobile || c.mobile2 || c.mobile3 || '—'}
                                {c.email ? ` · ${c.email}` : ''}
                              </div>
                              <div style={{ display: 'flex', gap: 3, marginTop: 2, flexWrap: 'wrap' }}>
                                {c.whatsapp_ready === true && (
                                  <span title="Verified on WhatsApp" style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: '#DCFCE7', color: '#15803D', fontWeight: 700 }}>💬 WA</span>
                                )}
                                {c.whatsapp_ready === false && (
                                  <span title="Not registered on WhatsApp" style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: '#FEE2E2', color: '#DC2626', fontWeight: 700 }}>⚠️ No WA</span>
                                )}
                                {(c.sms_ready === true || c.mobile || c.mobile2) && (
                                  <span title="SMS Capable" style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: '#E0F2FE', color: '#0369A1' }}>📱 SMS</span>
                                )}
                                {(c.email_ready === true || !!c.email) && (
                                  <span title="Email Capable" style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: '#F3E8FF', color: '#7E22CE' }}>📧 Email</span>
                                )}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                              <button onClick={() => c.type === 'customer' && navigate('/customers', { state: { editCustomerId: c.id } })}
                                title={c.type === 'customer' ? 'Edit Customer' : 'Edit available for customer only'}
                                disabled={c.type !== 'customer'}
                                style={{ background: 'none', border: 'none', cursor: c.type === 'customer' ? 'pointer' : 'not-allowed', opacity: c.type === 'customer' ? 1 : 0.4, fontSize: 16, padding: 2, lineHeight: 1 }}>✏️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {!loading && (
                  <PaginationBar
                    currentPage={composePage}
                    totalPages={totalComposePages}
                    totalItems={visibleCustomers.length}
                    pageSize={composePageSize}
                    onPageChange={setComposePage}
                    onPageSizeChange={setComposePageSize}
                    darkMode={darkMode}
                    border={border}
                    acc={acc}
                    muted={muted}
                    pg={pg}
                  />
                )}
              </div>
            </div>

            {/* Compose sidebar */}
            <div className="space-y-4">
              <div style={{ ...card, padding: 20 }}>
                <h3 style={{ ...pg, fontWeight: 700, marginBottom: 14, fontSize: 16 }}>Compose Message</h3>

                {/* Auto-generate template */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label style={{ ...muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                      Auto-Generate Message ({greetingTemplates.length} Indian Calendar Events)
                    </label>
                    {templateSearchTerm && (
                      <button type="button" onClick={() => setTemplateSearchTerm('')}
                        style={{ fontSize: 11, color: acc, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        Clear Search
                      </button>
                    )}
                  </div>

                  {/* Quick search input */}
                  <input
                    type="text"
                    placeholder="🔍 Search festival (e.g. Diwali, Holi, Pongal, Eid)..."
                    value={templateSearchTerm}
                    onChange={e => {
                      const val = e.target.value;
                      setTemplateSearchTerm(val);
                      if (val) setTemplateCategoryFilter('All');
                    }}
                    style={{ ...inp, padding: '7px 10px', fontSize: 12, marginBottom: 6 }}
                  />

                  {/* Template selector with grouped optgroups */}
                  <select value={selectedTemplate} onChange={e => handleTemplateSelect(e.target.value)} style={inp}>
                    <option value="">Choose a festival / template ({filteredTemplates.length} available)...</option>
                    {groupedTemplates.map(group => (
                      <optgroup key={group.category} label={group.category}>
                        {group.items.map(t => (
                          <option key={t.index} value={t.index}>{t.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  {/* Month & category pills */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {TEMPLATE_CATEGORIES.map(cat => {
                      const isSelected = templateCategoryFilter === cat;
                      const shortLabel = cat === 'All' ? 'All' :
                        cat.startsWith('January') ? 'Jan' :
                        cat.startsWith('February') ? 'Feb' :
                        cat.startsWith('March') ? 'Mar' :
                        cat.startsWith('April') ? 'Apr' :
                        cat.startsWith('May') ? 'May' :
                        cat.startsWith('June') ? 'Jun' :
                        cat.startsWith('July') ? 'Jul' :
                        cat.startsWith('August') ? 'Aug' :
                        cat.startsWith('September') ? 'Sep' :
                        cat.startsWith('October') ? 'Oct' :
                        cat.startsWith('November') ? 'Nov' :
                        cat.startsWith('December') ? 'Dec' :
                        cat === 'Business & Care' ? 'Business' :
                        cat === 'Personal & Care' ? 'Personal' : cat;
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setTemplateCategoryFilter(cat);
                            setTemplateSearchTerm('');
                          }}
                          title={cat}
                          style={{
                            fontSize: 10,
                            padding: '2px 7px',
                            borderRadius: 10,
                            border: `1px solid ${isSelected ? acc : border}`,
                            background: isSelected ? acc : (darkMode ? '#0F172A' : '#F1F5F9'),
                            color: isSelected ? '#fff' : (darkMode ? '#94A3B8' : '#64748B'),
                            cursor: 'pointer',
                            fontWeight: isSelected ? 700 : 500
                          }}
                        >
                          {shortLabel}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Channel */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ ...muted, fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Send Via</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {[['telegram', '📱 Telegram'], ['email', '📧 Email'], ['whatsapp', '💬 WhatsApp'], ['sms', '💌 SMS'], ['both', '📤 Telegram+Email']].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setChannel(v)} disabled={sending}
                        style={{ padding: '7px 0', fontSize: 11, borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                          background: channel === v ? acc : 'transparent',
                          color: channel === v ? '#fff' : (darkMode ? '#94A3B8' : '#64748B'),
                          border: `1px solid ${channel === v ? acc : (darkMode ? '#475569' : '#CBD5E1')}` }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* WhatsApp Web Connection Panel */}
                {channel === 'whatsapp' && (
                  <div style={{ marginBottom: 14, background: darkMode ? '#0F172A' : '#F0FDF4', borderRadius: 10, padding: 14, border: `1px solid ${darkMode ? '#065F46' : '#BBF7D0'}` }}>
                    <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 8, textTransform: 'uppercase', color: darkMode ? '#6EE7B7' : '#065F46' }}>💬 WhatsApp Web Session</label>
                    {waServiceDown ? (
                      <p style={{ fontSize: 12, color: '#EF4444' }}>WhatsApp connection is unavailable. Refresh the status after the service is running.</p>
                    ) : waConnected ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22C55E', display: 'inline-block' }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: darkMode ? '#6EE7B7' : '#065F46' }}>
                            Connected {waInfo?.pushname ? `as ${waInfo.pushname}` : ''} {waInfo?.phone ? `(${waInfo.phone})` : ''}
                          </span>
                        </div>
                        <p style={{ fontSize: 11, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 8 }}>Messages and attachments will be sent directly from this WhatsApp account.</p>
                        <button onClick={handleWaLogout} disabled={waLoggingOut || sending}
                          style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, opacity: waLoggingOut ? 0.5 : 1 }}>
                          {waLoggingOut ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      </div>
                    ) : waQR ? (
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: 12, marginBottom: 8, color: darkMode ? '#94A3B8' : '#64748B' }}>Scan this QR code with WhatsApp on your phone</p>
                        <img src={waQR} alt="WhatsApp QR Code" style={{ width: 220, height: 220, borderRadius: 8, margin: '0 auto', display: 'block' }} />
                        <p style={{ fontSize: 10, marginTop: 6, color: darkMode ? '#64748B' : '#94A3B8' }}>Open WhatsApp → Settings → Linked Devices → Link a Device</p>
                      </div>
                    ) : waState === 'error' ? (
                      <p style={{ fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>WhatsApp could not connect. Refresh the status after the service reconnects.</p>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 16 }}>
                        <div style={{ width: 20, height: 20, border: `3px solid ${acc}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 8px' }} />
                        <p style={{ fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>{waState === 'authenticated' ? 'Finishing WhatsApp connection... Please wait' : 'Initializing WhatsApp... Please wait'}</p>
                      </div>
                    )}
                    {waStatusError && <p role="alert" style={{ fontSize: 12, color: '#EF4444', marginTop: 8 }}>{waStatusError}</p>}
                    <button type="button" onClick={refreshWaStatus} disabled={waRefreshing || waLoggingOut}
                      style={{ marginTop: 8, fontSize: 11, padding: '5px 12px', borderRadius: 6, background: 'transparent', color: darkMode ? '#6EE7B7' : '#065F46', border: `1px solid ${darkMode ? '#065F46' : '#BBF7D0'}`, cursor: waRefreshing ? 'wait' : 'pointer', fontWeight: 600 }}>
                      {waRefreshing ? 'Checking connection...' : 'Refresh status'}
                    </button>
                  </div>
                )}

                {/* Message textarea */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ ...muted, fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Message</label>
                  <textarea value={message} onChange={e => setMessage(e.target.value)}
                    rows={7} placeholder={"Type your message...\n\nUse {name} for customer name\nUse {mobile} for phone number"}
                    style={{ ...inp, resize: 'vertical', minHeight: 120 }} />
                  <p style={{ ...muted, fontSize: 10, marginTop: 2 }}>{message.length} chars · {'{name}'} = cleaned name, {'{mobile}'} = phone</p>
                </div>

                {/* Shop Signature */}
                {shopSignature && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 6 }}>
                      <input type="checkbox" checked={appendSignature} onChange={e => setAppendSignature(e.target.checked)}
                        style={{ width: 16, height: 16, accentColor: acc }} />
                      <span style={{ ...muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Append Shop Signature</span>
                    </label>
                    {appendSignature && (
                      <div style={{ background: darkMode ? '#0F172A' : '#F0FDF4', border: `1px solid ${darkMode ? '#065F46' : '#BBF7D0'}`, borderRadius: 8, padding: 10, fontSize: 12, color: darkMode ? '#6EE7B7' : '#065F46', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {shopSignature.trim()}
                      </div>
                    )}
                  </div>
                )}

                {/* Attachment */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ ...muted, fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Attachment (optional)</label>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={e => setAttachment(e.target.files[0] || null)} style={{ display: 'none' }} />
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    style={{ ...inp, cursor: 'pointer', textAlign: 'center', color: darkMode ? '#94A3B8' : '#64748B', fontWeight: 600 }}>
                    {attachment ? `📎 ${attachment.name}` : '📎 Click to attach image/video/audio/doc'}
                  </button>
                  {attachment && (
                    <button onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      style={{ marginTop: 4, fontSize: 11, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      ✕ Remove attachment
                    </button>
                  )}
                </div>

                {/* Rate Limiting / Anti-block */}
                <div style={{ marginBottom: 14, background: darkMode ? '#0F172A' : '#F0F9FF', borderRadius: 8, padding: 12, border: `1px solid ${darkMode ? '#1E3A5F' : '#BAE6FD'}` }}>
                  <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 8, textTransform: 'uppercase', color: darkMode ? '#7DD3FC' : '#0369A1' }}>⏱️ Sending Settings</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={{ ...muted, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 3 }}>Delay (sec)</label>
                      <input type="number" min="0" max="120" value={delaySeconds} onChange={e => setDelaySeconds(Math.min(120, Math.max(0, parseInt(e.target.value) || 0)))}
                        style={{ ...inp, padding: '6px 8px', fontSize: 13, fontWeight: 700 }} />
                    </div>
                    <div>
                      <label style={{ ...muted, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 3 }}>Batch Limit</label>
                      <input type="number" min="1" max="500" value={batchSize} onChange={e => setBatchSize(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                        style={{ ...inp, padding: '6px 8px', fontSize: 13, fontWeight: 700 }} />
                    </div>
                  </div>
                  <p style={{ fontSize: 10, marginTop: 6, color: darkMode ? '#64748B' : '#94A3B8' }}>Sends up to {batchSize} messages per batch with a {delaySeconds}s delay between messages.</p>
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${darkMode ? '#1E3A5F' : '#BAE6FD'}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: 'pointer', fontSize: 11 }}>
                      <input
                        type="checkbox"
                        checked={antiBan}
                        onChange={e => setAntiBan(e.target.checked)}
                        style={{ width: 15, height: 15, marginTop: 1, accentColor: acc }}
                      />
                      <span>
                        <strong style={pg}>🛡️ Anti-ban Protection</strong>
                        <span style={{ display: 'block', ...muted, fontSize: 10 }}>
                          Random jitter (±30%) & cooling intervals to protect WhatsApp number from bans.
                        </span>
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: 'pointer', fontSize: 11 }}>
                      <input
                        type="checkbox"
                        checked={skipUnregistered}
                        onChange={e => setSkipUnregistered(e.target.checked)}
                        style={{ width: 15, height: 15, marginTop: 1, accentColor: acc }}
                      />
                      <span>
                        <strong style={pg}>⚡ Skip Non-WhatsApp Numbers</strong>
                        <span style={{ display: 'block', ...muted, fontSize: 10 }}>
                          Automatically skips numbers verified not to have WhatsApp to prevent spam flags.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {(channel === 'whatsapp' || channel === 'sms') && (
                  <div style={{ background: darkMode ? '#78350F44' : '#FFFBEB', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 11, color: darkMode ? '#FBBF24' : '#92400E' }}>
                    <strong>Note:</strong> {channel === 'whatsapp' ? 'Scan the QR code to connect. Successful sends are deselected so the next batch uses the remaining customers.' : 'SMS app will open for each customer with delay.'}
                  </div>
                )}

                {/* Sending progress */}
                {sendingProgress && (
                  <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, fontSize: 12, background: darkMode ? '#1E3A5F' : '#EFF6FF', color: darkMode ? '#7DD3FC' : '#1E40AF' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>📤 {channel === 'whatsapp' ? 'Processed' : 'Sending...'} {sendingProgress.sent}/{sendingProgress.total}</div>
                    <div style={{ height: 6, borderRadius: 3, background: darkMode ? '#0F172A' : '#DBEAFE', overflow: 'hidden', marginBottom: 4 }}>
                      <div style={{ height: '100%', borderRadius: 3, background: acc, width: `${(sendingProgress.sent / sendingProgress.total) * 100}%`, transition: 'width 0.3s' }} />
                    </div>
                    {sendingProgress.current && <div style={{ fontSize: 10 }}>Current: {sendingProgress.current}</div>}
                    <button onClick={() => sendingAbortRef.current = true}
                      style={{ marginTop: 6, fontSize: 10, color: '#EF4444', background: 'none', border: `1px solid #EF4444`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontWeight: 600 }}>
                      ⏹ Stop Sending
                    </button>
                  </div>
                )}

                {result && (
                  <div role="status" style={{ marginBottom: 12, padding: 10, borderRadius: 8, fontSize: 12,
                    background: result.success ? (darkMode ? '#064E3B' : '#F0FDF4') : (darkMode ? '#7F1D1D' : '#FEF2F2'),
                    color: result.success ? (darkMode ? '#6EE7B7' : '#065F46') : (darkMode ? '#FCA5A5' : '#991B1B') }}>
                    {result.success ? '✅' : '❌'} {result.msg}
                  </div>
                )}

                <button onClick={handleSend} disabled={sendDisabled}
                  style={{ ...btnPrimary(sendDisabled), width: '100%', padding: '12px 0' }}>
                  {sending ? 'Sending...' : channel === 'whatsapp' && !waConnected ? 'Connect WhatsApp to send' : `📤 Send to ${selectedCustomers.length} Customer${selectedCustomers.length !== 1 ? 's' : ''}`}
                </button>

                {/* Delivery Status & Log Card */}
                <div style={{ marginTop: 14, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 12, border: `1px solid ${border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: darkMode ? '#7DD3FC' : '#0369A1' }}>📋 Delivery Log</span>
                      <span style={{ fontSize: 10, ...muted }}>({deliveryLog.length})</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={handleSyncWaSent}
                        disabled={syncingWaSent}
                        style={{
                          padding: '3px 8px',
                          background: darkMode ? '#064E3B' : '#DCFCE7',
                          color: '#10B981',
                          border: '1px solid #10B981',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: syncingWaSent ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                        }}
                        title="Fetch sent messages from connected WhatsApp from the last 24 hours to automatically skip them"
                      >
                        {syncingWaSent ? '⏳ Syncing...' : '🔄 Sync WhatsApp Sent'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPasteModal(true)}
                        style={{
                          padding: '3px 8px',
                          background: darkMode ? '#1E293B' : '#F1F5F9',
                          color: pg.color,
                          border: `1px solid ${border}`,
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                        }}
                        title="Paste phone numbers that were already contacted to skip them"
                      >
                        ➕ Paste Sent Numbers
                      </button>
                      {failedCustomerIds.length > 0 && (
                        <button
                          type="button"
                          onClick={handleRetryFailedOnly}
                          style={{
                            padding: '3px 8px',
                            background: '#EF4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Select failed recipients to retry without duplicate sends"
                        >
                          🔄 Retry Failed ({failedCustomerIds.length})
                        </button>
                      )}
                      {deliveryLog.length > 0 && (
                        <button
                          type="button"
                          onClick={clearDeliveryLog}
                          style={{
                            padding: '3px 6px',
                            background: 'none',
                            border: `1px solid ${border}`,
                            color: muted.color,
                            borderRadius: 4,
                            fontSize: 10,
                            cursor: 'pointer',
                          }}
                          title="Clear temporary delivery log"
                        >
                          🗑️ Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {deliveryLog.length === 0 ? (
                    <div style={{ fontSize: 11, ...muted, padding: '4px 2px', lineHeight: 1.5 }}>
                      No delivery records yet today. Click <strong>🔄 Sync WhatsApp Sent</strong> to automatically load contacts messaged from WhatsApp, or <strong>➕ Paste Sent Numbers</strong> to skip contacts manually.
                    </div>
                  ) : (
                    <>

                    {/* Multi-channel fallback buttons for failed contacts */}
                    {failedCustomerIds.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, padding: '4px 6px', background: darkMode ? '#1E293B' : '#F1F5F9', borderRadius: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: muted.color, display: 'flex', alignItems: 'center' }}>Fallbacks:</span>
                        <button
                          type="button"
                          onClick={handleSwitchToSmsForFailed}
                          style={{
                            padding: '3px 8px',
                            background: '#0284C7',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Switch channel to SMS for failed recipients so they still get contacted"
                        >
                          📱 SMS for Failed ({failedCustomerIds.length})
                        </button>
                        <button
                          type="button"
                          onClick={handleSwitchToEmailForFailed}
                          style={{
                            padding: '3px 8px',
                            background: '#7C3AED',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Switch channel to Email for failed recipients who have email"
                        >
                          📧 Email for Failed
                        </button>
                      </div>
                    )}

                    {/* Filter pills */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      <button
                        type="button"
                        onClick={() => setDeliveryLogFilter('all')}
                        style={{
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          border: 'none',
                          cursor: 'pointer',
                          background: deliveryLogFilter === 'all' ? (darkMode ? '#334155' : '#E2E8F0') : 'transparent',
                          color: pg.color,
                        }}
                      >
                        All ({deliveryLog.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryLogFilter('sent')}
                        style={{
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          border: 'none',
                          cursor: 'pointer',
                          background: deliveryLogFilter === 'sent' ? (darkMode ? '#064E3B' : '#DCFCE7') : 'transparent',
                          color: '#10B981',
                        }}
                      >
                        ✓ Sent ({deliveryLog.filter(i => i.status === 'sent').length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryLogFilter('failed')}
                        style={{
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          border: 'none',
                          cursor: 'pointer',
                          background: deliveryLogFilter === 'failed' ? (darkMode ? '#7F1D1D' : '#FEE2E2') : 'transparent',
                          color: '#EF4444',
                        }}
                      >
                        ✗ Failed ({deliveryLog.filter(i => i.status === 'failed').length})
                      </button>
                    </div>

                    {/* Scrollable list of items */}
                    <div style={{ maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {filteredDeliveryLog.map((item, idx) => (
                        <div
                          key={item.id ? `${item.id}-${idx}` : `${item.phone}-${idx}`}
                          style={{
                            padding: '4px 6px',
                            borderRadius: 4,
                            background: darkMode ? '#1E293B' : '#fff',
                            border: `1px solid ${item.status === 'failed' ? (darkMode ? '#7F1D1D' : '#FECACA') : border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: 10,
                            gap: 6,
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 600, ...pg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.name || 'Unknown'} {item.phone ? <span style={{ ...muted, fontWeight: 400 }}>({item.phone})</span> : ''}
                            </div>
                            {item.status === 'failed' && item.error && (
                              <div style={{ color: '#EF4444', fontSize: 9, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.error}>
                                {item.error}
                              </div>
                            )}
                            {item.status === 'sent' && item.messageId && (
                              <div style={{ color: '#10B981', fontSize: 9, marginTop: 1 }}>
                                Confirmed: {item.messageId.slice(0, 14)}...
                              </div>
                            )}
                          </div>
                          <span
                            style={{
                              padding: '1px 5px',
                              borderRadius: 3,
                              fontSize: 9,
                              fontWeight: 700,
                              flexShrink: 0,
                              background: item.status === 'sent' ? (darkMode ? '#064E3B' : '#DCFCE7') : item.status === 'failed' ? (darkMode ? '#7F1D1D' : '#FEE2E2') : (darkMode ? '#334155' : '#E2E8F0'),
                              color: item.status === 'sent' ? '#10B981' : item.status === 'failed' ? '#EF4444' : muted.color,
                            }}
                          >
                            {item.status === 'sent' ? '✓ Sent' : item.status === 'failed' ? '✗ Failed' : 'Skipped'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              </div>

              <div style={{ background: darkMode ? '#78350F44' : '#FFFBEB', border: `1px solid ${darkMode ? '#92400E' : '#FDE68A'}`, borderRadius: 10, padding: 14 }}>
                <p style={{ fontWeight: 700, fontSize: 12, color: darkMode ? '#FCD34D' : '#92400E', marginBottom: 4 }}>⚙️ Setup Required</p>
                <p style={{ fontSize: 11, color: darkMode ? '#FBBF24' : '#B45309' }}>Connect WhatsApp using the QR code above. Configure Telegram Bot Token and SMTP in Settings for Telegram/Email. SMS opens your device app.</p>
              </div>
            </div>
          </div>
        )}

        {/* ═══════ IMPORT TAB ═══════ */}
        {mainTab === 'import' && (
          <div style={{ maxWidth: 1200 }}>
            {/* Step 1: Upload */}
            {importStep === 1 && (
              <div style={{ ...card, padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📥</div>
                <h2 style={{ ...pg, fontWeight: 800, marginBottom: 8 }}>Import Contacts</h2>
                <p style={{ ...muted, marginBottom: 24, fontSize: 14 }}>Upload Google Contacts .csv or .vcf file for smart cleaning, dedup and category tagging</p>
                <input ref={importInputRef} type="file" accept=".csv,.vcf,.vcard,text/csv,text/x-vcard" onChange={handleImportFileSelect} style={{ display: 'none' }} />
                <button onClick={() => importInputRef.current?.click()}
                  style={{ ...btnPrimary(false), padding: '14px 32px', fontSize: 16 }}>
                  📁 Select CSV/VCF File
                </button>
                <div style={{ ...muted, marginTop: 24, fontSize: 12, textAlign: 'left', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 16 }}>
                  <p style={{ fontWeight: 700, marginBottom: 8 }}>🔧 Smart Import Features:</p>
                  <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                    <li>Filter contacts by keyword (e.g., only import contacts with "customer" or "wholesaler" in name)</li>
                    <li>Auto-clean phone numbers (remove spaces, symbols, +91 prefix and keep only valid 10-digit mobile numbers)</li>
                    <li>Preserve the raw contact universe first; exact duplicate raw records are skipped only if they already exist in the imported universe</li>
                    <li>Extract multiple phone numbers, email & organization</li>
                    <li>Auto-categorize by name keyword: customer, family, friends, general</li>
                    <li>Import as Customer or Supplier</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 2: Configure */}
            {importStep === 2 && (
              <div style={{ ...card, padding: 24 }}>
                <h2 style={{ ...pg, fontWeight: 800, marginBottom: 4 }}>Configure Import</h2>
                <p style={{ ...muted, fontSize: 13, marginBottom: 20 }}>File: <strong>{importFile?.name}</strong></p>

                <div style={{ marginBottom: 20, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 12 }}>
                  <p style={{ ...pg, fontWeight: 700, fontSize: 14, margin: 0, marginBottom: 6 }}>🌐 Import to Contact Universe</p>
                  <p style={{ ...muted, fontSize: 12, margin: 0 }}>
                    Imported contacts are stored separately first. Use the contacts tab to convert selected imported contacts into customer or supplier masters.
                  </p>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ ...pg, fontWeight: 700, fontSize: 14, display: 'block', marginBottom: 8 }}>🔍 Filter by Keywords (optional)</label>
                  <p style={{ ...muted, fontSize: 12, marginBottom: 8 }}>Only import contacts whose name contains these words. Leave blank to import all contacts.</p>
                  <input type="text" value={importKeywords} onChange={e => setImportKeywords(e.target.value)}
                    placeholder="e.g. customer, wholesaler, retailer (comma separated)"
                    style={inp} />
                </div>

                <div style={{ marginBottom: 24, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={rebuildUniverse} onChange={e => setRebuildUniverse(e.target.checked)} style={{ width: 18, height: 18, accentColor: acc }} />
                    <span style={{ ...pg, fontWeight: 700, fontSize: 14 }}>♻️ Rebuild contact universe from this file</span>
                  </label>
                  <p style={{ ...muted, fontSize: 12, margin: '6px 0 0 28px' }}>Recommended for a complete import. This clears previously imported universe contacts for this tenant only, then imports every vCard as raw contact data.</p>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={resetImport}
                    style={{ padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', background: 'transparent', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${border}` }}>
                    ← Back
                  </button>
                  <button onClick={handleImport} disabled={importing}
                    style={{ ...btnPrimary(importing), flex: 1, padding: '12px 0' }}>
                    {importing ? '⏳ Importing...' : '🚀 Start Import'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Results */}
            {importStep === 3 && importResult && (
              <div style={{ ...card, padding: 24 }}>
                <h2 style={{ ...pg, fontWeight: 800, marginBottom: 16 }}>
                  {importResult.error ? '❌ Import Failed' : '✅ Import Complete'}
                </h2>

                {importResult.error ? (
                  <div style={{ background: darkMode ? '#7F1D1D' : '#FEF2F2', borderRadius: 8, padding: 16, color: darkMode ? '#FCA5A5' : '#991B1B', fontSize: 14 }}>
                    {importResult.error}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                      <p style={{ ...muted, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Total in File</p>
                      <p style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.total_in_file || 0}</p>
                    </div>
                    <div style={{ background: darkMode ? '#064E3B' : '#F0FDF4', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                      <p style={{ color: darkMode ? '#6EE7B7' : '#065F46', fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Imported</p>
                      <p style={{ color: darkMode ? '#6EE7B7' : '#065F46', fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.imported || 0}</p>
                    </div>
                    <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                      <p style={{ ...muted, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Filtered Out</p>
                      <p style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.filtered_out || 0}</p>
                    </div>
                    <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                      <p style={{ ...muted, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Duplicates Skipped</p>
                      <p style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.duplicates_skipped || 0}</p>
                    </div>
                    <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                      <p style={{ ...muted, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Dropped (No Mobile)</p>
                      <p style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.dropped_no_mobile || 0}</p>
                    </div>
                    <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                      <p style={{ ...muted, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Blocked (Already Exists)</p>
                      <p style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.blocked_existing_mobile || 0}</p>
                    </div>
                    <div style={{ background: darkMode ? '#78350F44' : '#FFFBEB', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                      <p style={{ color: darkMode ? '#FCD34D' : '#92400E', fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Needs Attention</p>
                      <p style={{ color: darkMode ? '#FCD34D' : '#92400E', fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.attention_count || 0}</p>
                    </div>
                    {!!importResult.category_counts && (
                      <div style={{ gridColumn: '1 / -1', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, padding: 16 }}>
                        <p style={{ ...muted, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>Auto Categories</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
                          <div style={{ textAlign: 'center' }}><p style={{ ...pg, margin: 0, fontWeight: 800 }}>{importResult.category_counts.customer || 0}</p><p style={{ ...muted, margin: 0, fontSize: 11 }}>Customer</p></div>
                          <div style={{ textAlign: 'center' }}><p style={{ ...pg, margin: 0, fontWeight: 800 }}>{importResult.category_counts.family || 0}</p><p style={{ ...muted, margin: 0, fontSize: 11 }}>Family</p></div>
                          <div style={{ textAlign: 'center' }}><p style={{ ...pg, margin: 0, fontWeight: 800 }}>{importResult.category_counts.friends || 0}</p><p style={{ ...muted, margin: 0, fontSize: 11 }}>Friends</p></div>
                          <div style={{ textAlign: 'center' }}><p style={{ ...pg, margin: 0, fontWeight: 800 }}>{importResult.category_counts.general || 0}</p><p style={{ ...muted, margin: 0, fontSize: 11 }}>General</p></div>
                        </div>
                      </div>
                    )}
                    {(importResult.errors || 0) > 0 && (
                      <div style={{ gridColumn: '1 / -1', background: darkMode ? '#7F1D1D' : '#FEF2F2', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                        <p style={{ color: darkMode ? '#FCA5A5' : '#991B1B', fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Errors</p>
                        <p style={{ color: darkMode ? '#FCA5A5' : '#991B1B', fontSize: 28, fontWeight: 800, margin: 0 }}>{importResult.errors}</p>
                        {Array.isArray(importResult.error_samples) && importResult.error_samples.length > 0 && (
                          <div style={{ marginTop: 10, textAlign: 'left', fontSize: 11, opacity: 0.9 }}>
                            {importResult.error_samples.map((s, i) => <div key={i}>• {s}</div>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
                  <button onClick={resetImport}
                    style={{ ...btnPrimary(false), flex: 1, padding: '12px 0' }}>
                    📥 Import Another File
                  </button>
                  <button onClick={() => setMainTab('contacts')}
                    style={{ padding: '12px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', background: 'transparent', color: acc, border: `1px solid ${acc}` }}>
                    View Contacts →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Paste Sent Numbers Modal */}
        {showPasteModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 16,
          }}>
            <div style={{
              ...card,
              width: '100%',
              maxWidth: 520,
              padding: 24,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ ...pg, margin: 0, fontSize: 16, fontWeight: 700 }}>
                  ➕ Paste Sent Phone Numbers to Skip
                </h3>
                <button
                  type="button"
                  onClick={() => { setShowPasteModal(false); setPastedNumbers(''); }}
                  style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', ...muted }}
                >
                  ✕
                </button>
              </div>
              <p style={{ ...muted, fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
                Paste any text containing mobile numbers (from WhatsApp chats, call logs, Excel, or notes).
                All 10-digit mobile numbers will be extracted and recorded as <strong>Sent</strong> so they are skipped from remaining sends to prevent duplicates.
              </p>
              <textarea
                rows={6}
                value={pastedNumbers}
                onChange={e => setPastedNumbers(e.target.value)}
                placeholder="Paste phone numbers or text here, e.g.:&#10;9876543210&#10;+91 91234 56789&#10;9988776655, 9811223344"
                style={{ ...inp, width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: 12, marginBottom: 16 }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => { setShowPasteModal(false); setPastedNumbers(''); }}
                  style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', ...pg, cursor: 'pointer', fontSize: 13 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApplyPastedNumbers}
                  style={{ ...btnPrimary(false), padding: '8px 18px', fontSize: 13 }}
                >
                  Mark as Sent & Skip
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Marketing;
